import React, { useEffect, useCallback } from "react";
import { compose } from "recompose";
import { connect } from "react-redux";
import {
  setExploreLocation,
  setExploreMapType,
  cancelExploreDataFetch,
  setIsRedirectToExplore,
  updateSelectedExploreView,
  setIsSideBarOpen
} from 'store/actions';
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import MapTypeManager from "./MapTypeManager"
import { getLocationFromMap, hasBoundsChanged } from "./helpers";
import _ from "lodash";
import PinManager from "./PinManager";
import MapOverlayShapeInfoBox from "./mapOverlayShapeInfoBox";
import ShapeManager from "./ShapeManager";
import MapDrawingManager from "./MapDrawingManager";
import MapPropertyDetailsBox from "./mapPropertyDetailsBox";
import ViewObjectManager from "./ViewObjectManager";
import SearchManager from "./SearchManager";
import SearchPinManager from "./SearchPinManager";
import Stack from "components/layout/Stack";
import { defaultMapStyles, blackGoldStyles } from './mapStyles';
import Label from '../views/objects/Label';
import {
  Paper,
  IconButton,
  Tooltip
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import { googleMapsApiKey } from "index";
import { fitBoundsWithZoom } from 'components/explore/gmap/helpers';

const scriptLibraries = ['geometry', 'drawing', 'places'];

function Gmap({ mapRef, pins, bounds, zoom, center, mapType, forceFetchAfterFitBounds, setExploreLocation, setExploreMapType, cancelExploreDataFetch,
  isRedirectToExplore, setIsRedirectToExplore, hasSelectedView, updateSelectedExploreView, isMobile, isSideBarOpen, setIsSideBarOpen }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey,
    libraries: scriptLibraries,
    version: '3.64'
  })
  // const mapRef = React.useRef(null);
  const initialized = React.useRef(false);
  const prevZoom = React.useRef(null);

  const [click, setClick] = React.useState(null);

  useEffect(() => {
    if (!mapRef?.current || !bounds?.length) return;

    const mapLocation = getLocationFromMap(mapRef.current);
    if (hasBoundsChanged(mapLocation, bounds) || forceFetchAfterFitBounds) {
      fitBoundsWithZoom(mapRef.current, {south: bounds[0], north: bounds[1], west: bounds[2], east: bounds[3]})
    }
  }, [bounds]);

  useEffect(() => {
    if (!mapRef?.current || !mapType) return;

    const currentMapType = mapRef.current.getMapTypeId();
    if (mapType !== currentMapType) {
      mapRef.current.setMapTypeId(mapType)
    }
  }, [mapType]);

  useEffect(() => {
    if (isRedirectToExplore && pins.length && mapRef?.current) {
      fitBoundsToPins();
      setIsRedirectToExplore(false);
    }
  }, [isRedirectToExplore, pins]);

  const onZoomChanged = () => {
    if (!mapRef.current) return;
    const newZoom = mapRef.current.getZoom()
    if (prevZoom.current == null) { // onZoomChanged fires whenever zoom is set, even if set to same value. Need to track previous zoom to see if it actually changes.
      prevZoom.current = newZoom;
    } else if (prevZoom.current !== newZoom) {
      prevZoom.current = newZoom;
      cancelDataFetch();
    }
  }

  const cancelExploreDataFetchThrottled = _.throttle(cancelExploreDataFetch, 1000); // Set throttle equal to debounce delay. No need to send cancel events faster than setExploreLocation can be called and new requests generated
  function cancelDataFetch() {
    cancelExploreDataFetchThrottled();
  }

  const onBoundsChangedDebounced = _.debounce(onBoundsChanged, 1000);
  function onBoundsChanged() {
    if (!mapRef?.current) return;
    const newLocation = getLocationFromMap(mapRef.current);

    const updateLocation = () => {
      if (hasBoundsChanged(newLocation, bounds) || forceFetchAfterFitBounds) {
        console.log(newLocation)
        setExploreLocation(newLocation);
        if (isRedirectToExplore) {
          console.log('isRedirectToExplore')
          updateSelectedExploreView({ zoomLevel: newLocation.zoom, latitude: newLocation.center?.lat, longitude: newLocation.center?.lng, bounds: newLocation.bounds });
        }
      }
    }

    if (!initialized.current) {
      initialized.current = true;
      if (zoom == null || center == null) { // Only fitBounds on first mount of map when zoom/center are null- if leaving Explore and returning, keep map at existing zoom/center
        fitBoundsWithZoom(mapRef.current, {south: bounds[0], north: bounds[1], west: bounds[2], east: bounds[3]})
        if (isRedirectToExplore) {
          updateLocation();
        }
      }
    } else {
      updateLocation();
    }
  }

  const handleMapTypeChange = () => {
    if (!mapRef.current) return;
    const newMapType = mapRef.current.getMapTypeId();
    if (newMapType !== mapType) {
      setExploreMapType(newMapType);
      if (hasSelectedView) {
        updateSelectedExploreView({ mapViewType: newMapType });
      }
    }
  }

  const getMapStyle = useCallback(() => {
    const defaultStyle = new window.google.maps.StyledMapType(defaultMapStyles, { name: 'Default', maxZoom: 22 });
    const styledMapType = new window.google.maps.StyledMapType(blackGoldStyles, { name: 'Spooky', maxZoom: 22 });
    return [{
      id: 'default',
      style: defaultStyle
    }, {
      id: 'halloween_satellite',
      style: styledMapType
    }]
  }, [])

  const onLoad = React.useCallback(map => {
    mapRef.current = map;
    const mapStyles = getMapStyle();
    mapStyles.forEach(el => {
      mapRef.current.mapTypes.set(el.id, el.style);
    })
    mapRef.current.setMapTypeId(mapType);
    mapRef.current.setTilt(0);
  }, [getMapStyle, mapType])
  if (loadError) {
    console.log('error loading map');
    return null;
  }

  function fitBoundsToPins() { // Used only when isRedirectToExplore is true (ie coming from a View on Explore action). Data filter will be on a fixed list of prop IDs, so we are able to best fit on it
    const bounds = new window.google.maps.LatLngBounds();

    if (pins.length) {
      pins.forEach(pin => {
        if (pin.latitude && pin.longitude) {
          bounds.extend({ lat: pin.latitude, lng: pin.longitude });
        }
      });

      fitBoundsWithZoom(mapRef.current, bounds, 19);
    }
  }

  function handleClick(e) {
    if (e?.latLng) {
      const lat = e?.latLng.lat();
      const lng = e?.latLng.lng();

      setClick({ lat, lng })
    }
  }

  const onSideBarToggleClick = () => {
    setIsSideBarOpen(!isSideBarOpen)
  }

  return (
    isLoaded ?
    <GoogleMap
      mapContainerStyle={containerStyle}
      onLoad={onLoad}
      center={center}
      zoom={zoom}
      //onIdle={handleIdle}
      clickableIcons={false}
      onBoundsChanged={onBoundsChangedDebounced}
      onMapTypeIdChanged={handleMapTypeChange}
      onZoomChanged={onZoomChanged}
      onDragStart={cancelDataFetch}
      onClick={handleClick}
      options={{
        gestureHandling: 'greedy',
        cameraControl: false,
        zoomControl: isMobile ? false : true,
        fullscreenControlOptions: {
          position: window.google.maps.ControlPosition.BOTTOM_RIGHT
        },
        mapTypeControl: isMobile ? false : true,
        rotateControl: !isMobile,
        mapTypeControlOptions: {
          style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
          position: isMobile ? window.google.maps.ControlPosition.TOP_RIGHT : window.google.maps.ControlPosition.TOP_RIGHT,
          mapTypeIds: ['default', 'roadmap', 'terrain', 'hybrid', 'satellite', 'halloween_satellite']
        },
        minZoom: 4,
        restriction: {
          latLngBounds: {
            west: -179.9999999,
            east: 179.9999999,
            north: 85,
            south: -85
          },
          strictBounds: true
        }
      }}
    >
      <ShapeManager />
      <PinManager />
      <ViewObjectManager mapRef={mapRef}/>
      <SearchPinManager />
      {isMobile && (
              <Tooltip title={`Map Options`} placement="bottom">
              <Paper style={{ position: 'absolute', top: 0, margin: 10 }}>    
                  <IconButton                 
                      onClick={onSideBarToggleClick}
                      >
                      <MenuIcon />
                  </IconButton>                        
                </Paper>  
              </Tooltip>    
          )}
      <SearchManager mapRef={mapRef} />
      <MapTypeManager mapRef={mapRef} isMobile={isMobile} />
      <Stack style={{position: 'absolute', top: 10, left: 10}}>
        <Stack horizontal grow>
          <MapDrawingManager />
          <Label position={click} />
        </Stack>
      </Stack>
      <Stack style={{position: 'absolute', top: 82, left: 10}}>
        <MapOverlayShapeInfoBox />
      </Stack>
      <Stack style={isMobile ? { position: 'absolute', bottom: 125, width: '95%', marginLeft: 10, marginRight: 10} : {position: 'absolute', top: 224, right: 10}}>
        <MapPropertyDetailsBox isMobile={isMobile} />
      </Stack>
    </GoogleMap>
    : <div>Loading Map</div>
  )
}

const containerStyle = {
  flexGrow: 1,
  display:"flex",
  position: 'relative',
  width: '100%'
};

function mapState({ exploreMap, exploreViews, appState }) {
  return {
    pins: exploreMap.pins,
    isRedirectToExplore: exploreMap.isRedirectToExplore,
    bounds: exploreMap.bounds,
    zoom: exploreMap.zoom,
    center: exploreMap.center,
    mapType: exploreMap.mapType,
    forceFetchAfterFitBounds: exploreMap.forceFetchAfterFitBounds,
    hasSelectedView: exploreViews.selectedView != null,
    isMobile: appState.isMobile,
    isSideBarOpen: exploreViews.isSideBarOpen
  };
}

const mapDispatch = {
  setExploreLocation,
  setExploreMapType,
  cancelExploreDataFetch,
  setIsRedirectToExplore,
  updateSelectedExploreView,
  setIsSideBarOpen
}

export default compose(
  connect(mapState, mapDispatch)
)(Gmap);